/**
 * Shared parts of Cài đặt › Module: links, names in the reader's language, status badges, the
 * values of a version, errors and warnings in plain words.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '../../api';
import type {
  BilingualText,
  ModuleBranchId,
  ModuleCall,
  ModuleField,
  ModuleHealth,
  ModuleInstance,
  ModuleInstanceStatus,
  ModuleManifest,
  ModuleTestStep,
  ModuleVersion,
  ModuleVersionStatus,
  ModuleWarning,
} from '../../api/areas/modules';
import { Badge, type Tone } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { errorHint } from '../../components/Write';
import i18n, { useLanguage } from '../../i18n';
import { formatCount } from '../../lib/format';
import { fieldLabel, OnOffBadge, SecretStatusLine, tx } from '../integrations/shared';
import { isWriteOnly, pick } from './fieldKinds';

type LooseT = (key: string, options?: Record<string, unknown>) => string;
/** A translation of the `modules` namespace for a key built at run time (the key exists or `fallback`). */
export function mx(key: string, fallback: string, options?: Record<string, unknown>): string {
  const full = `modules:${key}`;
  return i18n.exists(full) ? (i18n.t as unknown as LooseT)(full, options) : fallback;
}

/** `/settings?tab=modules&branch=…[&module=…&instance=…][&pane=…]`. */
export function modulesUrl(branch: ModuleBranchId, module?: string, instance?: string, pane?: string): string {
  const p = new URLSearchParams({ tab: 'modules', branch });
  if (module && instance) {
    p.set('module', module);
    p.set('instance', instance);
  }
  if (pane) p.set('pane', pane);
  return `/settings?${p.toString()}`;
}

/** Bilingual server text in the reader's language. */
export function useText(): (text: BilingualText | null | undefined) => string {
  const lang = useLanguage();
  return (text) => pick(text, lang);
}

/** "Kết nối chính" for the default instance, else its name or key. */
export function useInstanceLabel(): (i: Pick<ModuleInstance, 'key' | 'display_name'>) => string {
  const { t } = useTranslation('modules');
  return (i) => i.display_name ?? (i.key === 'default' ? t('instance.defaultName') : i.key);
}

export const STATUS_TONE: Readonly<Record<ModuleInstanceStatus, Tone>> = {
  NEW: 'neutral',
  DRAFT: 'pending',
  TESTED: 'info',
  PENDING: 'pending',
  ACTIVE: 'ok',
  DISABLED: 'neutral',
};

export const HEALTH_TONE: Readonly<Record<ModuleHealth, Tone>> = { healthy: 'ok', degraded: 'warn', down: 'crit', unknown: 'neutral' };

export const VERSION_TONE: Readonly<Record<ModuleVersionStatus, Tone>> = {
  DRAFT: 'pending',
  PENDING_APPROVAL: 'pending',
  ACTIVE: 'ok',
  SUPERSEDED: 'neutral',
  REJECTED: 'crit',
  CANCELLED: 'neutral',
};

export function InstanceStatusBadge({ status }: { status: ModuleInstanceStatus }) {
  const { t } = useTranslation('modules');
  return (
    <Badge tone={STATUS_TONE[status]} title={t(`statusHint.${status}`)}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function HealthBadge({ health }: { health: ModuleHealth }) {
  const { t } = useTranslation('modules');
  return (
    <Badge tone={HEALTH_TONE[health]} title={t(`healthHint.${health}`)}>
      {t(`health.${health}`)}
    </Badge>
  );
}

export function VersionStatusBadge({ status }: { status: ModuleVersionStatus }) {
  const { t } = useTranslation('modules');
  return <Badge tone={VERSION_TONE[status]}>{t(`versionStatus.${status}`)}</Badge>;
}

/** A capability by its name (`modules:capability.*`); one the console does not know shows its code. */
export function useCapabilityName(): (code: string) => string {
  const { t } = useTranslation('modules');
  return (code) => mx(`capability.${code}`, t('capability.other', { code }));
}

/** What a step's or a call's code means: a failure class, a provider refusal, else "other answer". */
export function useCodeMeaning(): (code: string) => string {
  const { t } = useTranslation('modules');
  return (code) => mx(`failure.${code}`, mx(`refusal.${code}`, t('otherCode')));
}

/** One step of a connection test. */
export function StepLine({ step }: { step: ModuleTestStep }) {
  const { t } = useTranslation('modules');
  const meaning = useCodeMeaning();
  const tone: Tone = step.outcome === 'OK' ? 'ok' : step.outcome === 'FAILED' ? 'crit' : 'neutral';
  return (
    <li className="grid gap-0.5 text-sm">
      <span className="flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{t(`test.stepOutcome.${step.outcome}`)}</Badge>
        <span className="font-medium">{t(`test.step.${step.step}`)}</span>
        {step.duration_ms ? <span className="text-xs text-muted-foreground">{t('test.duration', { ms: formatCount(step.duration_ms) })}</span> : null}
      </span>
      {step.code ? (
        <span className="text-xs">
          {meaning(step.code)} <code className="rounded bg-muted px-1 text-muted-foreground">{step.code}</code>
        </span>
      ) : null}
      {step.detail ? <span className="break-words font-mono text-xs text-muted-foreground">{step.detail}</span> : null}
    </li>
  );
}

/** The operation of a call in words: the connection test, an admin action by its label, else as recorded. */
export function useOperationName(manifest: ModuleManifest): (call: Pick<ModuleCall, 'operation'>) => string {
  const { t } = useTranslation('modules');
  const text = useText();
  return (call) => {
    if (call.operation === 'test') return t('logs.operation.test');
    if (call.operation.startsWith('action:')) {
      const key = call.operation.slice('action:'.length);
      const spec = manifest.admin_actions.find((a) => a.key === key);
      return t('logs.operation.action', { name: spec ? text(spec.label) : key });
    }
    return call.operation;
  };
}

/** A stored value as text: a choice by its label, a number with its unit, a switch as on / off. */
export function useValueText(): (field: ModuleField, value: unknown) => ReactNode {
  const { t } = useTranslation('modules');
  const text = useText();
  return (field, value) => {
    if (value === undefined || value === null || value === '') {
      return (
        <span className="text-muted-foreground">
          {t('config.unset')}
          {field.default !== null ? ` · ${t('form.defaultValue', { value: Array.isArray(field.default) ? field.default.join(', ') : String(field.default) })}` : null}
        </span>
      );
    }
    const k = field.kind;
    switch (k.type) {
      case 'boolean':
        return <OnOffBadge on={value === true} />;
      case 'select': {
        const c = k.options.find((o) => o.value === value);
        return c ? text(c.label) : <code className="text-xs">{String(value)}</code>;
      }
      case 'multi_select':
        return Array.isArray(value) ? value.map((v) => text(k.options.find((o) => o.value === v)?.label) || String(v)).join(', ') : String(value);
      case 'integer':
        return `${formatCount(Number(value))}${k.unit ? ` ${t(`unit.${k.unit}`)}` : ''}`;
      case 'url':
      case 'path':
      case 'text':
        return <code className="break-all text-xs">{String(value)}</code>;
      default:
        return String(value);
    }
  };
}

/** The settings of one version: values, secrets (status only), on / off, reason. */
export function VersionValues({ version, fields, compact = false }: { version: ModuleVersion; fields: readonly ModuleField[]; compact?: boolean }) {
  const { t } = useTranslation('modules');
  const text = useText();
  const valueText = useValueText();
  const plain = fields.filter((f) => !isWriteOnly(f.kind));
  const secret = fields.filter((f) => isWriteOnly(f.kind));
  return (
    <div className="space-y-3">
      <dl className={compact ? 'grid gap-1.5 text-xs' : 'grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2'}>
        <div className="min-w-0 space-y-0.5">
          <dt className="text-xs font-medium text-muted-foreground">{t('config.enabledField')}</dt>
          <dd className="text-sm">
            <OnOffBadge on={version.enabled} />
          </dd>
        </div>
        {plain.map((f) => (
          <div key={f.key} className="min-w-0 space-y-0.5">
            <dt className="text-xs font-medium text-muted-foreground">{text(f.label)}</dt>
            <dd className="text-sm break-words">{valueText(f, version.values[f.key])}</dd>
          </div>
        ))}
      </dl>
      {secret.length ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('config.secrets')}</p>
          <div className="grid items-start gap-3 sm:grid-cols-2">
            {secret.map((f) => {
              const status = version.secrets[f.key];
              return status ? <SecretStatusLine key={f.key} label={text(f.label)} status={status} /> : null;
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface FieldErrorDetail {
  field: string;
  code: string;
}

function fieldErrorsOf(details: unknown): FieldErrorDetail[] {
  if (!details || typeof details !== 'object' || !('fields' in details) || !Array.isArray(details.fields)) return [];
  return details.fields.filter((f): f is FieldErrorDetail => !!f && typeof f === 'object' && typeof (f as FieldErrorDetail).field === 'string' && typeof (f as FieldErrorDetail).code === 'string');
}

/** `values.site_key` / `secrets.secret_key` named by the field's label. */
export function useFieldName(fields: readonly ModuleField[]): (path: string) => string {
  const { t } = useTranslation('modules');
  const text = useText();
  return (path) => {
    if (path === 'enabled') return t('config.enabledField');
    const key = path.replace(/^(values|secrets|input)\./, '');
    const f = fields.find((x) => x.key === key);
    return f ? text(f.label) : fieldLabel(path);
  };
}

/** The error of a change, a test or an action, in plain words (field errors by the field's name). */
export function ModuleError({ error, fields = [] }: { error: unknown; fields?: readonly ModuleField[] }) {
  const { t } = useTranslation('modules');
  const fieldName = useFieldName(fields);
  if (!isApiError(error)) {
    return (
      <Callout tone="crit" title={t('errors.failed')}>
        {error instanceof Error ? error.message : null}
      </Callout>
    );
  }
  const details = error.details as { retry_after_seconds?: unknown } | undefined;
  const retry = typeof details?.retry_after_seconds === 'number' ? details.retry_after_seconds : null;
  const message =
    error.code === 'TEST_RATE_LIMITED' && retry !== null
      ? t('test.rateLimited', { seconds: retry })
      : mx(`errors.code.${error.code}`, tx(`errors.code.${error.code}`, t('errors.code.other')));
  const fieldErrors = error.code === 'VALIDATION_FAILED' ? fieldErrorsOf(error.details) : [];
  const hint = errorHint(error.code);
  return (
    <div role="alert" className="space-y-1.5 rounded-md border border-critical/30 bg-critical-soft p-3 text-sm">
      <p className="font-medium">{message}</p>
      {fieldErrors.length ? (
        <ul className="list-disc space-y-0.5 pl-5">
          {fieldErrors.map((f, i) => (
            <li key={`${f.field}-${i}`}>
              <span className="font-medium">{fieldName(f.field)}</span>: {mx(`errors.field.${f.code}`, tx(`errors.field.${f.code}`, t('errors.field.other', { code: f.code })))}
            </li>
          ))}
        </ul>
      ) : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <p className="text-xs text-muted-foreground">{t('errors.meta', { code: error.code, requestId: error.requestId })}</p>
    </div>
  );
}

/** Notes on a saved draft (the host's or the module's). */
export function ModuleWarnings({ warnings, fields }: { warnings: readonly ModuleWarning[]; fields: readonly ModuleField[] }) {
  const { t } = useTranslation('modules');
  const fieldName = useFieldName(fields);
  if (!warnings.length) return null;
  return (
    <Callout tone="warn" title={t('errors.warning.title')}>
      <ul className="list-disc space-y-1 pl-4">
        {warnings.map((w, i) => (
          <li key={`${w.code}-${w.field ?? ''}-${i}`}>
            {mx(`errors.warning.${w.code}`, tx(`warnings.${w.code}`, t('errors.warning.other', { code: w.code })))}
            {w.field ? <span className="text-xs text-muted-foreground"> ({fieldName(w.field)})</span> : null}
          </li>
        ))}
      </ul>
    </Callout>
  );
}

/** "Server provided documentation": a filed path or a public page. */
export function DocList({ docs }: { docs: ModuleManifest['docs'] }) {
  const { t } = useTranslation('modules');
  return (
    <ul className="grid gap-1 text-sm">
      {docs.map((d) => (
        <li key={d.location} className="break-all">
          {d.kind === 'public' ? (
            <a href={d.location} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline">
              {d.location}
            </a>
          ) : (
            <span className="text-muted-foreground">{t('card.filedDocument', { path: d.location })}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
